/**
 * OTP Verification Page
 * Updated: Jan 21, 2026 - FrogBasket branding
 */

import { useState, useRef, useEffect, FormEvent, KeyboardEvent } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { authService } from '@/lib/auth';
import { logger } from '@/lib/logger';

export default function OTPPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const phone = location.state?.phone as string | undefined;

  const [code, setCode] = useState(['', '', '', '', '', '']);
  const [isVerifying, setIsVerifying] = useState(false);
  const [error, setError] = useState('');
  const [countdown, setCountdown] = useState(60);
  const [canResend, setCanResend] = useState(false);

  const inputRefs = [
    useRef<HTMLInputElement>(null),
    useRef<HTMLInputElement>(null),
    useRef<HTMLInputElement>(null),
    useRef<HTMLInputElement>(null),
    useRef<HTMLInputElement>(null),
    useRef<HTMLInputElement>(null),
  ];

  // Redirect if no phone number
  useEffect(() => {
    if (!phone) {
      navigate('/login', { replace: true });
    }
  }, [phone, navigate]);

  // Countdown timer
  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    } else {
      setCanResend(true);
    }
  }, [countdown]);

  // Auto-focus first input
  useEffect(() => {
    inputRefs[0].current?.focus();
  }, []);

  const handleChange = (index: number, value: string) => {
    // Only allow numbers
    if (value && !/^\d$/.test(value)) return;

    const newCode = [...code];
    newCode[index] = value;
    setCode(newCode);
    setError('');

    // Auto-focus next input
    if (value && index < 5) {
      inputRefs[index + 1].current?.focus();
    }

    // Auto-submit when all 6 digits are entered
    if (index === 5 && value) {
      const fullCode = [...newCode.slice(0, 5), value].join('');
      verifyOTP(fullCode);
    }
  };

  const handleKeyDown = (index: number, e: KeyboardEvent<HTMLInputElement>) => {
    // Handle backspace
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      inputRefs[index - 1].current?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').trim();
    
    // Only allow 6-digit numbers
    if (!/^\d{6}$/.test(pastedData)) return;

    const newCode = pastedData.split('');
    setCode(newCode);
    setError('');

    // Focus last input
    inputRefs[5].current?.focus();

    // Auto-submit
    verifyOTP(pastedData);
  };

  const verifyOTP = async (otpCode: string) => {
    if (!phone) return;
    if (otpCode.length !== 6) {
      setError('Please enter all 6 digits');
      return;
    }

    setIsVerifying(true);
    setError('');

    try {
      await authService.verifyOTP(phone, otpCode);
      logger.info('OTP verified, redirecting to orders');
      navigate('/orders', { replace: true });
    } catch (err: any) {
      logger.error('OTP verification failed', err);
      setError(err.response?.data?.message || 'Invalid code. Please try again.');
      setCode(['', '', '', '', '', '']);
      inputRefs[0].current?.focus();
    } finally {
      setIsVerifying(false);
    }
  };

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    const fullCode = code.join('');
    verifyOTP(fullCode);
  };

  const handleResend = async () => {
    if (!phone || !canResend) return;

    try {
      await authService.requestOTP(phone);
      setCountdown(60);
      setCanResend(false);
      setError('');
      setCode(['', '', '', '', '', '']);
      inputRefs[0].current?.focus();
      logger.info('OTP resent');
    } catch (err: any) {
      logger.error('OTP resend failed', err);
      setError(err.response?.data?.message || 'Failed to resend code');
    }
  };

  if (!phone) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-500 via-green-600 to-teal-700 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="bg-white rounded-2xl shadow-2xl p-8">
          {/* Header */}
          <div className="text-center mb-8">
            {/* FrogBasket Logo */}
            <div className="flex justify-center mb-4">
              <img 
                src="/frog_logo.png" 
                alt="FrogBasket" 
                className="w-20 h-20 object-contain"
                onError={(e) => {
                  (e.target as HTMLImageElement).style.display = 'none';
                }}
              />
            </div>
            <h1 className="text-3xl font-bold mb-2">
              <span className="text-emerald-600">Frog</span>
              <span className="text-amber-500">Basket</span>
            </h1>
            <p className="text-gray-600">Rider App</p>
          </div>

          <h2 className="text-xl font-semibold text-gray-900 mb-2 text-center">Enter Verification Code</h2>
          <p className="text-sm text-gray-600 mb-6 text-center">
            We sent a 6-digit code to <strong>{phone}</strong>
          </p>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* OTP Input */}
            <div className="flex gap-2 justify-center">
              {code.map((digit, index) => (
                <input
                  key={index}
                  ref={inputRefs[index]}
                  type="text"
                  inputMode="numeric"
                  maxLength={1}
                  value={digit}
                  onChange={(e) => handleChange(index, e.target.value)}
                  onKeyDown={(e) => handleKeyDown(index, e)}
                  onPaste={index === 0 ? handlePaste : undefined}
                  className="w-12 h-14 text-center text-2xl font-bold rounded-lg border-2 border-gray-300 bg-white focus:outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500 transition-colors"
                  disabled={isVerifying}
                />
              ))}
            </div>

            {/* Error Message */}
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-center">
                {error}
              </div>
            )}

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isVerifying || code.some(d => !d)}
              className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-semibold py-3 px-4 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isVerifying ? 'Verifying...' : 'Verify'}
            </button>

            {/* Resend Code */}
            <div className="text-center">
              {canResend ? (
                <button
                  type="button"
                  onClick={handleResend}
                  className="text-sm text-emerald-600 hover:text-emerald-700 hover:underline"
                >
                  Resend Code
                </button>
              ) : (
                <p className="text-sm text-gray-600">
                  Resend code in <strong>{countdown}s</strong>
                </p>
              )}
            </div>

            {/* Back to Login */}
            <div className="text-center">
              <button
                type="button"
                onClick={() => navigate('/login')}
                className="text-sm text-gray-500 hover:text-gray-700"
              >
                ← Back to Login
              </button>
            </div>
          </form>

          <div className="mt-6 text-center text-sm text-gray-600">
            <p>Rider access only</p>
          </div>
        </div>
      </div>
    </div>
  );
}
